import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { Observable, Observer } from 'rxjs';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private bearerPrefix = 'Bearer ';

  constructor(
    private userService: UserService,
    private keycloakService: KeycloakService
  ) {}

  addTokenToHeader(headersArg?: HttpHeaders): Observable<HttpHeaders> {
    return Observable.create(async (observer: Observer<any>) => {
      let headers = headersArg;
      if (!headers) {
        headers = new HttpHeaders();
      }
      try {
        const token: string = await this.keycloakService.getToken();
        headers = headers.set('Authorization', this.bearerPrefix + token);
        observer.next(headers);
        observer.complete();
      } catch (error) {
        observer.error(error);
      }
    });
  }

  logout(options: Keycloak.KeycloakLogoutOptions = {}): Promise<void> {
    return new Promise(async (resolve, reject) => {
      await this.keycloakService
        .logout()
        .then((success) => {
          resolve();
        })
        .catch((error) => {
          resolve();
        });
    });
  }

  login(options: Keycloak.KeycloakLoginOptions = {}): Promise<any> {
    return new Promise((resolve, reject) => {
      this.keycloakService
        .login(options)
        .then(async (res) => {
          await this.userService
            .getUserRolesPermissions(this.keycloakService.getUsername())
            .toPromise();
          resolve(res);
        })
        .catch((error) => {
          reject('An error happened during the login.');
        });
    });
  }

  isLoggedIn(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        await this.updateToken(20);
        resolve(true);
      } catch (error) {
        resolve(false);
      }
    });
  }

  updateToken(minValidity = 5): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      if (!this.keycloakService.isTokenExpired) {
        reject(false);
        return;
      }

      this.keycloakService
        .updateToken(minValidity)
        .then((refreshed) => {
          resolve(refreshed);
        })
        .catch((error) => {
          reject('Failed to refresh the token, or the session is expired');
        });
    });
  }

  isTokenExpired(minValidity = 0): boolean {
    return this.keycloakService.isTokenExpired(minValidity);
  }

  getToken(): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        await this.updateToken(10);
        resolve(this.keycloakService.getToken());
      } catch (error) {
        this.login();
      }
    });
  }

  clearToken(): void {
    this.keycloakService.clearToken();
  }
}
